import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { CountriesState } from '@nibtravel/shared/data-access/countries';
import { QuoteState } from '@nibtravel/shared/data-access/quote';
import { QuoteConfigState } from '@nibtravel/shared/data-access/quote-config';
import { ValidationsHelper } from '@nibtravel/shared/ui/forms/validation';
import { Qqc } from '@nibtravel/shared/util/e2e';
import { IFormFieldValidation, QuoteType } from '@nibtravel/shared/util/models';
import { Observable, Subject } from 'rxjs';
import { takeUntil, map, tap } from 'rxjs/operators';

import { DestinationPipe } from './destination.pipe';
import { DestinationValidator } from './destination.validators';
import { AlertType } from '@nibtravel/shared/ui/components/alert';
import { isAustralia } from '../../../../../shared/util/common/isAustralia';
import { InternationalDomesticService } from '../../../../feature-shell/qqc/src/lib/qqc.international-domestic.service';
import { AutocompleteComponent } from '@nibtravel/shared/ui/forms/autocomplete';
import { QqcSelector } from '../../../../feature-shell/qqc/src/lib/qqc.selector';
import { PopularCountrySuggestionsComponent } from './popularCountrySuggestions/popularCountrySuggestions.component';

@Component({
  selector: 'nibtravel-qqc-destination',
  templateUrl: './qqc-destination.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QqcDestinationComponent implements OnInit, OnDestroy {
  @HostBinding('class') hostClass = 'qqc-destination';

  e2eId = Qqc;
  @Input() qqcForm!: FormGroup;
  @Select(QuoteState.quoteType)
  quoteType$!: Observable<QuoteType>;
  @Input() previousQuoteDestinations!: string[];
  types = QuoteType;
  @Input() currentRoute = '';
  options$!: Observable<Record<string, any>[]>;
  control!: AbstractControl;
  validations!: IFormFieldValidation[];
  AlertType = AlertType;
  private readonly controlName = 'destinationCountryCodes';
  private destroy$ = new Subject<void>();
  @ViewChild('autocomplete') autocomplete!: AutocompleteComponent;
  @ViewChild('popularCountrySuggestions')
  popularCountrySuggestions!: PopularCountrySuggestionsComponent;

  showAustraliaAlert$ = this.store
    .select(QuoteState.destinationCountryCodes)
    .pipe(
      map((destinationCountryCodes) => {
        return isAustralia(destinationCountryCodes);
      })
    );

  isShowPopularDestinationPicker$ = this.store
    .select(QqcSelector.currentRoute)
    .pipe(
      map((route) => {
        const defaultRootRoutes = ['', '/'];
        if (defaultRootRoutes.includes(route)) {
          return true;
        }
        return false;
      })
    );

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private destinationPipe: DestinationPipe,
    private internationalDomesticService: InternationalDomesticService
  ) {}

  ngOnInit(): void {
    this.quoteType$
      .pipe(takeUntil(this.destroy$))
      .subscribe((quoteType) => this.setupControl(quoteType));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupControl(quoteType: QuoteType): void {
    if (this.qqcForm) {
      this.setupValidations();
      let initialValue: string | string[] | null = this.getSnapshot(
        QuoteState.destinationCountryCodes
      );
      switch (quoteType) {
        case QuoteType.SingleTrip:
          this.options$ = this.get(CountriesState.destinationCountries);
          initialValue = initialValue?.length ? initialValue : null;
          break;
        case QuoteType.MultiTrip:
          this.options$ = this.get(CountriesState.destinationRegions);
          initialValue = initialValue?.length ? initialValue[0] : null;
          break;
      }

      this.createFormControl(initialValue);
    }
  }

  get showInternationalToDomesticAlert() {
    const changeFromInternationToDomestic =
      this.internationalDomesticService.getInternationalDomesticStatus(
        this.previousQuoteDestinations
      ).changeFromInternationToDomestic;

    if (['', '/', 'plans'].includes(this.currentRoute)) return false;
    return changeFromInternationToDomestic;
  }

  private setupValidations(): void {
    const homeCountry = this.getSnapshot(QuoteState.homeCountry);
    if (homeCountry) {
      const quoteType = this.getSnapshot(QuoteState.quoteType);
      const configFn = this.getSnapshot((state) =>
        QuoteConfigState.travelRestrictions(state.quoteConfig, quoteType)
      );
      const config = configFn(homeCountry);

      this.validations = [
        {
          ...ValidationsHelper.Required,
          message: () => 'Please enter all destinations and regions.',
        },
        {
          ...DestinationValidator.RestrictedValidation(config, homeCountry),
          message: (
            control: AbstractControl,
            validation: Record<string, any>
          ) => {
            return `Sorry, we’re currently unable to provide cover for ${this.destinationPipe.transform(
              validation.destinations
            )}. Please remove it from your quote.`;
          },
        },
      ];
    }
  }

  private createFormControl(initialValue: string | string[] | null) {
    if (this.qqcForm.get(this.controlName)) {
      this.qqcForm.removeControl(this.controlName);
    }

    this.control = this.fb.control(initialValue);
    this.qqcForm.addControl(this.controlName, this.control);
  }

  /** Abstract the store select to provide syntactic suger, which improves readability */
  private get<T>(selector: (state: any, ...states: any[]) => T): Observable<T> {
    return this.store.select<T>(selector);
  }

  /** Abstract the store selectSnapshot to provide syntactic suger, which improves readability */
  private getSnapshot<T>(selector: (state: any, ...states: any[]) => T): T {
    return this.store.selectSnapshot<T>(selector);
  }
}
